/**
 * ConversationView (06 S7) — assembles PresenceHeader + MessageList + Composer.
 * Marks the conversation read on open, surfaces the in-chat search overlay
 * (S21, local-only), and the connection banner when WS is reconnecting.
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { PresenceHeader } from './PresenceHeader';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import styles from './ConversationView.module.css';

/** Stable empty array so the zustand snapshot reference stays constant when a
 *  conversation has no messages yet (avoids getSnapshot-not-cached loop). */
const NO_MESSAGES: never[] = [];

export function ConversationView({ chatId }: { chatId: string }) {
  const conv = useChatStore((s) => s.getConversation(chatId));
  const messages = useChatStore((s) => s.messagesByConv[chatId] ?? NO_MESSAGES);
  const me = useChatStore((s) => s.me);
  const typing = useChatStore((s) => (s.typing[chatId] ?? []).some((u) => u !== me.id));
  const markRead = useChatStore((s) => s.markRead);
  const connection = useChatStore((s) => s.connection);
  const searchOpen = useUiStore((s) => s.searchOpen);
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);

  const [query, setQuery] = useState('');

  useEffect(() => {
    markRead(chatId);
  }, [chatId, markRead, messages.length]);

  useEffect(() => {
    setSearchOpen(false);
    setQuery('');
  }, [chatId, setSearchOpen]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return messages.filter((m) => !m.deleted && m.text.toLowerCase().includes(q));
  }, [query, messages]);

  if (!conv) {
    return <div className={styles.missing}>Conversation not found.</div>;
  }

  return (
    <section className={styles.view} aria-label="Conversation">
      <PresenceHeader conv={conv} />

      {searchOpen && (
        <div className={styles.searchBar}>
          <input
            className={styles.searchInput}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search messages on this device"
            aria-label="Search messages"
          />
          <span className={styles.searchCount}>
            {query ? `${matches.length} found` : ''}
          </span>
          <button className={styles.searchNav} aria-label="Previous match"><ChevronUp size={18} /></button>
          <button className={styles.searchNav} aria-label="Next match"><ChevronDown size={18} /></button>
          <button
            className={styles.searchNav}
            onClick={() => { setSearchOpen(false); setQuery(''); }}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>
      )}
      {searchOpen && (
        <p className={styles.searchHint}>Search covers messages stored on this device.</p>
      )}

      {connection === 'reconnecting' && (
        <div className={styles.connBanner}>Waiting for network…</div>
      )}
      {connection === 'connecting' && (
        <div className={styles.connBanner}>Connecting…</div>
      )}

      <MessageList conv={conv} messages={messages} typing={typing} />
      <Composer conv={conv} />
    </section>
  );
}
